import { Dayjs } from "dayjs";
import { SeriesOptionsType } from "highcharts";
import { each } from "lodash";
import { Product, Seller } from "types";
import { api } from "../api/api";
import { queryClient } from "../api/queryClient";
import { config } from "../config";
import { purchaseTypes } from "../models/Transaction";
import { AnalyticsMode } from "../stores/reports/analyticsFilterStore";

const MAX_PRODUCTS_PER_CHART = 8;

export interface RevenueReportParams {
    seller: Seller;
    from: Dayjs;
    to: Dayjs;
    mode: AnalyticsMode;
    products: ID[];
}

export const getRevenueReport = async (params: RevenueReportParams): Promise<SeriesOptionsType[]> => {
    const { seller } = params;
    const index = config.elasticsearch.indexes.transaction;
    const { data } = await api.post(`/elasticsearch/${index}/_search?seller=${seller.id}`, getPayload(params));
    return getReport(data);
};

const getPayload = ({ seller, from, to, mode, products }: RevenueReportParams) => {
    const aggs =
        mode === AnalyticsMode.CASH_FLOW
            ? getAggsForCashFlow(seller.timezoneName, getProductFilters(products))
            : getAggsForRealizedEarnings(seller.timezoneName);

    const filter = {
        size: 0,
        aggs: {
            revenueHistogram: {
                nested: { path: "items" },
                aggs: {
                    filtered: {
                        filter: { bool: { must: [] } },
                        aggs: { filtered_by_product: { filters: getProductFilters(products), aggs } },
                    },
                },
            },
        },
        query: {
            bool: {
                must: [
                    { nested: { path: "items", query: { bool: { must: [] } } } },
                    { terms: { type: purchaseTypes } },
                    { term: { "seller.id": seller.id } },
                ],
            },
        },
    } as any;

    const transactionFilter = filter.query.bool.must;
    const range = { from: from.valueOf(), to: to.valueOf() };
    if (mode === AnalyticsMode.CASH_FLOW) {
        transactionFilter.push({ range: { createdAt: range } });
    } else {
        const rangeFilter = { range: { "items.realizedAt": range } };
        transactionFilter[0].nested.query.bool.must.push(rangeFilter);
        filter.aggs.revenueHistogram.aggs.filtered.filter.bool.must.push(rangeFilter);
    }

    return filter;
};

const getAggsForCashFlow = (timezoneName: string, productFilters: any) => ({
    transaction: {
        // We had to use a hack to make this work. Please refer AnalyticsService if you're going to use this aggregation elsewhere.
        reverse_nested: {},
        aggs: {
            daily: {
                date_histogram: { field: "createdAt", interval: "day", time_zone: timezoneName },
                aggs: {
                    items: {
                        nested: { path: "items" },
                        aggs: {
                            filtered_by_product: {
                                filters: productFilters,
                                aggs: { amount_stats: { stats: { field: "items.gross" } } },
                            },
                        },
                    },
                },
            },
        },
    },
});

const getAggsForRealizedEarnings = (timezoneName: string) => ({
    daily: {
        date_histogram: { field: "items.realizedAt", interval: "day", time_zone: timezoneName },
        aggs: { amount_stats: { stats: { field: "items.gross" } } },
    },
});

const getProductFilters = (products: ID[]) => {
    const filter: any = { filters: { all: { match_all: { boost: 1 } } } };

    // Create a date_histogram for each product only if there are <= 8 products
    if (products.length <= MAX_PRODUCTS_PER_CHART) {
        for (const productId of products) {
            filter.filters[productId] = { term: { "items.orderItem.product.id": productId } };
        }
    } else {
        filter.filters.selected = { terms: { "items.orderItem.product.id": products } };
    }

    return filter;
};

/**
 * Process the response from the Elasticsearch call. This will plot the data on the chart by creating new series
 * and adding points on the series
 */
const getReport = (resp: any) => {
    if (!resp.aggregations) {
        console.error("Facets not found");
        return [];
    }

    // For every facet, create a new series on the chart
    const seriesData: any = [];
    each(resp.aggregations.revenueHistogram.filtered.filtered_by_product.buckets, (s: any, productId: string) => {
        const product = queryClient.getQueryData<Product>(["products", productId]);
        const seriesOptions = {
            name: product?.name ?? productId,
            data: [],
            color: "",
            marker: { enabled: false },
        };
        if (productId === "all") {
            // This is data for all listings which will use the default 'blue' color
            seriesOptions.name = "Total (All Products)";
        } else if (productId === "selected") {
            // This is the 'total' data for selected listings only
            seriesOptions.name = "Selected Products";
        }

        const chartData = s.daily ? getSeriesDataForRealizedEarnings(s) : getSeriesDataForCashFlow(s, productId);

        // Check if we have any data to plot
        if (chartData.length > 0) {
            // We have data to plot, create the series, do not redraw the chart yet and do not animate
            seriesOptions.data = chartData;
            seriesData.push(seriesOptions);
        }
    });

    return seriesData;
};

const getSeriesDataForCashFlow = (s: any, productId: string) => {
    return s.transaction.daily.buckets.map((r: any) => {
        const amountStats = r.items.filtered_by_product.buckets[productId].amount_stats;
        return { x: r.key, y: amountStats.sum || 0 };
    });
};

const getSeriesDataForRealizedEarnings = (s: any) => {
    return s.daily.buckets.map((r: any) => ({ x: r.key, y: r.amount_stats.sum || 0 }));
};
